import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiPlusBold } from 'react-icons/pi';
import { Button, Input, Text } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type CandidateInstallment = {
    installment_id: number | null;
    installment_number: number | null;
    total_installments: number | null;
    label: string;
    due_date: string | null;
    installment_amount: number;
    suggested_commission: number;
    record_id: number | null;
    needs_materialize?: boolean;
    already_in_month?: boolean;
};

type CandidateDeal = {
    deal_id: number;
    deal_number: string | null;
    internal_number: string | null;
    display_sale_number: string | null;
    title: string | null;
    amount: number;
    finish_date: string | null;
    installments: CandidateInstallment[];
};

type CandidatesHint = {
    code: string;
    message: string;
    display_sale_number?: string | null;
    owner_name?: string | null;
    owner_user_id?: number | null;
    deal_id?: number;
    exclusion_ids?: number[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    sellerUserId: number;
    month: string;
    sellerName?: string;
    allowOtherSellers?: boolean;
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

function formatDueDate(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR');
}

function installmentOptionLabel(
    inst: CandidateInstallment,
    alreadyLabel: string,
): string {
    const parts = [inst.label];
    const due = formatDueDate(inst.due_date);
    if (due) parts.push(due);
    if (inst.suggested_commission > 0) parts.push(brl(inst.suggested_commission));
    if (inst.already_in_month) parts.push(alreadyLabel);
    return parts.join(' · ');
}

export default function ManualSaleInclusionModal({
    open,
    onClose,
    sellerUserId,
    month,
    sellerName,
    allowOtherSellers = false,
}: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [dealId, setDealId] = useState<number | null>(null);
    const [installmentId, setInstallmentId] = useState<number | null | undefined>(undefined);
    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(search.trim()), 300);
        return () => window.clearTimeout(id);
    }, [search]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setDebounced('');
            setDealId(null);
            setInstallmentId(undefined);
            setNotes('');
            setError(null);
        }
    }, [open]);

    const candidatesQuery = useQuery<{ data: CandidateDeal[]; hint: CandidatesHint | null }>({
        queryKey: ['commissioning', 'manual-inclusion-candidates', sellerUserId, month, debounced],
        enabled: open && sellerUserId > 0,
        queryFn: () =>
            api
                .get(`/commissioning/sellers/${sellerUserId}/manual-inclusions/${month}/candidates`, {
                    params: debounced ? { q: debounced } : undefined,
                })
                .then((r) => r.data),
    });

    const deals = candidatesQuery.data?.data ?? [];
    const hint = candidatesQuery.data?.hint ?? null;
    const selectedDeal = useMemo(
        () => deals.find((d) => d.deal_id === dealId) ?? null,
        [deals, dealId],
    );
    const attachableInstallments = useMemo(
        () => (selectedDeal?.installments ?? []).filter((i) => !i.already_in_month),
        [selectedDeal],
    );
    const selectedInstallment = useMemo(() => {
        if (!selectedDeal || installmentId === undefined) return null;
        return selectedDeal.installments.find((i) => i.installment_id === installmentId) ?? null;
    }, [selectedDeal, installmentId]);

    useEffect(() => {
        if (!selectedDeal) {
            setInstallmentId(undefined);
            return;
        }
        const first = attachableInstallments[0] ?? selectedDeal.installments[0];
        setInstallmentId(first ? first.installment_id : null);
    }, [selectedDeal?.deal_id, attachableInstallments.length]);

    const includeMutation = useMutation({
        mutationFn: () =>
            api.post(`/commissioning/sellers/${sellerUserId}/manual-inclusions/${month}`, {
                ploomes_deal_id: dealId,
                commission_installment_id: installmentId ?? null,
                notes: notes.trim() || null,
            }),
        onSuccess: async (res: { data?: { data?: {
            schedule_included?: boolean;
            included_count?: number;
            months?: string[];
            message?: string;
        } } }) => {
            const data = res?.data?.data;
            if (data?.schedule_included && (data.included_count ?? 0) > 1) {
                toast.success(t('v2.commissioning.history_month.manual_include_toast_schedule', {
                    count: String(data.included_count ?? 0),
                    months: String(data.months?.length ?? 0),
                }));
            } else if (data?.message) {
                toast.success(data.message);
            }
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-month-history', String(sellerUserId)],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-timeline', String(sellerUserId)],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'manual-inclusion-candidates', sellerUserId, month],
            });
            onClose();
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                    ?.response?.data;
            const firstErr = msg?.errors
                ? Object.values(msg.errors).flat()[0]
                : msg?.message;
            setError(firstErr || t('v2.commissioning.history_month.manual_include_error'));
        },
    });

    const restoreMutation = useMutation({
        mutationFn: async () => {
            const ids = hint?.exclusion_ids ?? [];
            for (const id of ids) {
                await api.delete(`/commissioning/sellers/${sellerUserId}/month-exclusions/${id}`);
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-month-history', String(sellerUserId), month],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'manual-inclusion-candidates', sellerUserId, month],
            });
            setError(null);
            onClose();
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                    ?.response?.data;
            const firstErr = msg?.errors
                ? Object.values(msg.errors).flat()[0]
                : msg?.message;
            setError(firstErr || t('v2.commissioning.history_month.manual_include_error'));
        },
    });

    const canSubmit =
        dealId != null
        && installmentId !== undefined
        && !selectedInstallment?.already_in_month
        && (selectedInstallment?.suggested_commission ?? 0) > 0
        && !includeMutation.isPending;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="xl"
            title={t('v2.commissioning.history_month.manual_include_title')}
            description={t(
                allowOtherSellers
                    ? 'v2.commissioning.history_month.manual_include_help_leadership'
                    : 'v2.commissioning.history_month.manual_include_help',
                {
                    month,
                    seller: sellerName || '—',
                },
            )}
            footer={(
                <>
                    <Button type="button" variant="outline" onClick={onClose}>
                        {t('v2.commissioning.history_month.manual_include_cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={!canSubmit}
                        onClick={() => {
                            setError(null);
                            includeMutation.mutate();
                        }}
                        className="gap-1.5"
                    >
                        <PiPlusBold className="size-3.5" />
                        {includeMutation.isPending
                            ? t('v2.commissioning.history_month.manual_include_saving')
                            : t('v2.commissioning.history_month.manual_include_submit')}
                    </Button>
                </>
            )}
        >
            <div className="space-y-4">
                <div>
                    <Text className="mb-1.5 text-xs font-semibold text-gray-600 dark:text-zinc-300">
                        {t('v2.commissioning.history_month.manual_include_search')}
                    </Text>
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('v2.commissioning.history_month.manual_include_search_ph')}
                    />
                </div>

                <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-gray-200 p-2 dark:border-zinc-700">
                    {candidatesQuery.isLoading && (
                        <p className="px-2 py-3 text-xs text-gray-500">
                            {t('v2.commissioning.history_month.manual_include_loading')}
                        </p>
                    )}
                    {!candidatesQuery.isLoading && deals.length === 0 && !hint && (
                        <p className="px-2 py-3 text-xs text-gray-500">
                            {t('v2.commissioning.history_month.manual_include_empty')}
                        </p>
                    )}
                    {!candidatesQuery.isLoading && deals.length === 0 && hint && (
                        <div className="space-y-2 rounded-lg bg-amber-50 px-3 py-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                            <p>{hint.code === 'excluded'
                                ? t('v2.commissioning.history_month.manual_include_excluded_hint')
                                : hint.message}</p>
                            {hint.code === 'excluded' && (hint.exclusion_ids?.length ?? 0) > 0 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={restoreMutation.isPending}
                                    onClick={() => {
                                        setError(null);
                                        restoreMutation.mutate();
                                    }}
                                    className="text-xs"
                                >
                                    {restoreMutation.isPending
                                        ? t('v2.commissioning.history_month.manual_include_restoring')
                                        : t('v2.commissioning.history_month.manual_include_restore')}
                                </Button>
                            )}
                        </div>
                    )}
                    {deals.map((deal) => (
                        <button
                            key={deal.deal_id}
                            type="button"
                            onClick={() => setDealId(deal.deal_id)}
                            className={`w-full rounded-lg px-3 py-2 text-left transition ${
                                dealId === deal.deal_id
                                    ? 'bg-orange-50 ring-1 ring-orange-300 dark:bg-orange-950/30 dark:ring-orange-700'
                                    : 'hover:bg-gray-50 dark:hover:bg-zinc-800'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {deal.display_sale_number || deal.internal_number || `#${deal.deal_number}`}
                                </span>
                                <span className="text-xs tabular-nums text-gray-500">{brl(deal.amount)}</span>
                            </div>
                            <p className="mt-0.5 truncate text-xs text-gray-500">{deal.title || '—'}</p>
                        </button>
                    ))}
                </div>

                {selectedDeal && (
                    <div className="space-y-3">
                        <div>
                            <Text className="mb-1.5 text-xs font-semibold text-gray-600 dark:text-zinc-300">
                                {t('v2.commissioning.history_month.manual_include_installment')}
                            </Text>
                            <select
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                value={installmentId === null ? 'null' : String(installmentId ?? '')}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setInstallmentId(v === 'null' ? null : Number(v));
                                }}
                            >
                                {selectedDeal.installments.map((inst) => (
                                    <option
                                        key={`${inst.installment_id ?? 'deal'}-${inst.label}`}
                                        value={inst.installment_id === null ? 'null' : String(inst.installment_id)}
                                        disabled={Boolean(inst.already_in_month)}
                                    >
                                        {installmentOptionLabel(
                                            inst,
                                            t('v2.commissioning.history_month.manual_include_already'),
                                        )}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedInstallment?.already_in_month && (
                            <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                                {t('v2.commissioning.history_month.manual_include_already_hint')}
                            </p>
                        )}

                        {selectedInstallment && !selectedInstallment.already_in_month && (
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2.5 dark:border-emerald-800 dark:bg-emerald-950/30">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                                    {t('v2.commissioning.history_month.manual_include_amount_label')}
                                </p>
                                <p className="mt-0.5 text-lg font-black tabular-nums text-emerald-800 dark:text-emerald-200">
                                    {brl(selectedInstallment.suggested_commission)}
                                </p>
                                {formatDueDate(selectedInstallment.due_date) && (
                                    <p className="mt-1 text-[11px] font-medium tabular-nums text-emerald-800/90 dark:text-emerald-200/90">
                                        {t('v2.commissioning.history_month.manual_include_due', {
                                            date: formatDueDate(selectedInstallment.due_date)!,
                                        })}
                                    </p>
                                )}
                                <p className="mt-1 text-[11px] text-emerald-700/80 dark:text-emerald-300/80">
                                    {t('v2.commissioning.history_month.manual_include_amount_hint')}
                                </p>
                            </div>
                        )}

                        <div>
                            <Text className="mb-1.5 text-xs font-semibold text-gray-600 dark:text-zinc-300">
                                {t('v2.commissioning.history_month.manual_include_notes')}
                            </Text>
                            <Input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('v2.commissioning.history_month.manual_include_notes_ph')}
                            />
                        </div>
                    </div>
                )}

                {error && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
                        {error}
                    </p>
                )}
            </div>
        </V2Modal>
    );
}

